// AIReportsScreen — director's AI-report feed (docs/miniapp-m3-tz.md §3.8).
// M-3: a simple list backed by the live leader-risk digest (+ any push history).
// Tap → /ai-reports/:id (AIReportDetailScreen renders the full body). Full
// report generation is a later phase.
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { fetchAiReports } from '../../api/director'
import type { AiReport } from '../../api/types'
import { Badge, Button, EmptyState, Icon, ScreenSkeleton, t } from '../../design'

// iso (2026-06-02) → locale-neutral DD.MM.YYYY for the chip; empty stays empty.
function fmtDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '')
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso
}

function kindLabel(kind: string): string {
  return kind.includes('weekly') ? t('air.weekly') : t('air.daily')
}

function ReportCard({ r, onClick }: { r: AiReport; onClick: () => void }) {
  const weekly = r.kind.includes('weekly')
  return (
    <div className="card card-16" onClick={onClick} style={{ cursor: 'pointer' }}>
      <div className="row middle gap-2" style={{ marginBottom: 6 }}>
        <Badge tone={weekly ? 'ai' : 'neutral'} sm>
          <Icon.Sparkle size={10} sw={2.5} />{kindLabel(r.kind)}
        </Badge>
        {r.date && <span className="t-caption dim flex-1" style={{ textAlign: 'right' }}>{fmtDate(r.date)}</span>}
      </div>
      <div className="t-body-s" style={{ marginBottom: 4 }}>
        {weekly ? t('air.weeklyTitle', { date: fmtDate(r.date) }) : t('air.dailyTitle', { date: fmtDate(r.date) })}
      </div>
      <div className="t-caption" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {r.excerpt}
      </div>
      <div className="row middle gap-1" style={{ marginTop: 8, color: 'var(--brand-500)' }}>
        <span className="t-caption-s" style={{ color: 'var(--brand-500)', fontWeight: 600 }}>{t('air.readMore')}</span>
        <Icon.ArrowRight size={13} sw={2.5} />
      </div>
    </div>
  )
}

export function AIReportsScreen() {
  const navigate = useNavigate()
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['ai-reports'],
    queryFn: fetchAiReports,
  })

  if (isLoading) return <ScreenSkeleton kind="list" />
  if (isError) {
    return (
      <div className="scroll-y" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 14, padding: 24 }}>
        <Icon.CloudOff size={30} sw={2} style={{ color: 'var(--text-3)' }} />
        <div className="t-h2">{t('air.loadErr')}</div>
        <Button variant="secondary" icon={<Icon.Refresh size={16} sw={2} />} onClick={() => refetch()}>{t('btn.retry')}</Button>
      </div>
    )
  }

  const reports = data?.reports ?? []

  return (
    <div className="scroll-y" style={{ flex: 1, padding: '12px 16px 24px' }}>
      <div className="t-h1" style={{ marginBottom: 12 }}>{t('air.title')}</div>
      {reports.length === 0 ? (
        <EmptyState icon={<Icon.Sparkle size={28} />} title={t('air.empty')} hint={t('air.emptyHint')} />
      ) : (
        <div className="col" style={{ gap: 10 }}>
          {reports.map((r) => (
            <ReportCard key={r.id} r={r} onClick={() => navigate(`/ai-reports/${encodeURIComponent(r.id)}`)} />
          ))}
        </div>
      )}
    </div>
  )
}
